// packages/web/src/stores/teacherLearningStore.ts
import { create } from 'zustand';

export interface Exercise {
  id: string;
  title: string;
  initial_question: string;
  category: 'math' | 'physics' | 'chemistry' | 'others';
  teacher_requirement?: string;
  originalTeacherRequirement?: string; // 用于追踪原始值，判断是否被修改
  knowledge_points?: KnowledgePoint[];
  selected?: boolean;
  topicId?: string;
  imageAnalysisId?: string; // 用于更新操作
}

export interface KnowledgePoint {
  name: string;
  difficulty: 1 | 2 | 3;
  currentScore?: number;
  status?: string;
  firstMentioned?: string;
  lastUpdated?: string;
  lastReasoning?: string;
  isNewlyDiscovered?: boolean;
}

export interface TeachingMaterial {
  id: string;
  filename: string | null;
  file_path: string | null;
  file_type: 'text' | 'pdf' | 'docx' | 'image';
  content_summary: string;
  created_at: string;
  last_used_at: string;
}

export interface SavedTopic {
  id: string;
  title: string;
  knowledge_points: KnowledgePoint[];
}

interface TeacherLearningState {
  // 上传相关
  exerciseImagePath: string | null;
  exerciseImagePreview: string | null;
  materialText: string;
  materialFileBase64: string | null;
  materialFileName: string | null;
  materialFileType: string | null;
  skipMaterial: boolean;
  historyMaterials: TeachingMaterial[];
  
  // AI处理进度
  currentStep: 1 | 2 | 3;
  step1Status: 'idle' | 'loading' | 'success' | 'error';
  step2Status: 'idle' | 'loading' | 'success' | 'error' | 'skipped';
  step3Status: 'idle' | 'loading' | 'success' | 'error';
  errorMessage: string | null;
  
  // 题目相关
  exercises: Exercise[];
  savedTopics: SavedTopic[];
  
  // 模态窗口
  showCropModal: boolean;
  showKnowledgePointsModal: boolean;
  showAssignModal: boolean;
  selectedExerciseForDetail: Exercise | null;
  
  // 布置作业
  assignMode: 'class' | 'student';
  selectedClasses: string[];
  selectedStudents: string[];
  
  // Actions
  setExerciseImage: (path: string | null, preview: string | null) => void;
  setMaterialText: (text: string) => void;
  setMaterialFile: (base64: string, fileName: string, fileType: string) => void;
  setSkipMaterial: (skip: boolean) => void;
  setHistoryMaterials: (materials: TeachingMaterial[]) => void;
  setCurrentStep: (step: 1 | 2 | 3) => void;
  setStepStatus: (step: 1 | 2 | 3, status: string) => void;
  setErrorMessage: (message: string | null) => void;
  setExercises: (exercises: Exercise[]) => void;
  updateExercise: (id: string, updates: Partial<Exercise>) => void;
  toggleExerciseSelection: (id: string) => void;
  selectAllExercises: () => void;
  deselectAllExercises: () => void;
  deleteExercise: (id: string) => void;
  setSavedTopics: (topics: SavedTopic[]) => void;
  setShowCropModal: (show: boolean) => void;
  setShowKnowledgePointsModal: (show: boolean) => void;
  setShowAssignModal: (show: boolean) => void;
  setSelectedExerciseForDetail: (exercise: Exercise | null) => void;
  setAssignMode: (mode: 'class' | 'student') => void;
  setSelectedClasses: (classes: string[]) => void;
  setSelectedStudents: (students: string[]) => void;
  reset: () => void;
}

export const useTeacherLearningStore = create<TeacherLearningState>((set) => ({
  // 初始状态
  exerciseImagePath: null,
  exerciseImagePreview: null,
  materialText: '',
  materialFileBase64: null,
  materialFileName: null,
  materialFileType: null,
  skipMaterial: false,
  historyMaterials: [],
  currentStep: 1,
  step1Status: 'idle',
  step2Status: 'idle',
  step3Status: 'idle',
  errorMessage: null,
  exercises: [],
  savedTopics: [],
  showCropModal: false,
  showKnowledgePointsModal: false,
  showAssignModal: false,
  selectedExerciseForDetail: null,
  assignMode: 'class',
  selectedClasses: [],
  selectedStudents: [],
  
  // Actions
  setExerciseImage: (path, preview) => set({ 
    exerciseImagePath: path, 
    exerciseImagePreview: preview 
  }),
  
  setMaterialText: (text) => set({ materialText: text }),
  
  setMaterialFile: (base64, fileName, fileType) => set({ 
    materialFileBase64: base64, 
    materialFileName: fileName, 
    materialFileType: fileType 
  }),
  
  setSkipMaterial: (skip) => set({ skipMaterial: skip }),
  
  setHistoryMaterials: (materials) => set({ historyMaterials: materials }),
  
  setCurrentStep: (step) => set({ currentStep: step }),
  
  setStepStatus: (step, status) => {
    if (step === 1) {
      set({ step1Status: status as any });
    } else if (step === 2) {
      set({ step2Status: status as any });
    } else if (step === 3) {
      set({ step3Status: status as any });
    }
  },
  
  setErrorMessage: (message) => set({ errorMessage: message }),
  
  setExercises: (exercises) => set({ exercises }),
  
  updateExercise: (id, updates) => set((state) => ({
    exercises: state.exercises.map(ex => 
      ex.id === id ? { ...ex, ...updates } : ex
    )
  })),
  
  toggleExerciseSelection: (id) => set((state) => ({
    exercises: state.exercises.map(ex => 
      ex.id === id ? { ...ex, selected: !ex.selected } : ex
    )
  })),
  
  selectAllExercises: () => set((state) => ({
    exercises: state.exercises.map(ex => ({ ...ex, selected: true }))
  })),
  
  deselectAllExercises: () => set((state) => ({
    exercises: state.exercises.map(ex => ({ ...ex, selected: false }))
  })),
  
  deleteExercise: (id) => set((state) => ({
    exercises: state.exercises.filter(ex => ex.id !== id)
  })),
  
  setSavedTopics: (topics) => set({ savedTopics: topics }),
  
  setShowCropModal: (show) => set({ showCropModal: show }),
  
  setShowKnowledgePointsModal: (show) => set({ showKnowledgePointsModal: show }),
  
  setShowAssignModal: (show) => set({ showAssignModal: show }),
  
  setSelectedExerciseForDetail: (exercise) => set({ 
    selectedExerciseForDetail: exercise 
  }),
  
  setAssignMode: (mode) => set({ assignMode: mode }),
  
  setSelectedClasses: (classes) => set({ selectedClasses: classes }),
  
  setSelectedStudents: (students) => set({ selectedStudents: students }),
  
  reset: () => set({
    exerciseImagePath: null,
    exerciseImagePreview: null,
    materialText: '',
    materialFileBase64: null,
    materialFileName: null,
    materialFileType: null,
    skipMaterial: false,
    currentStep: 1,
    step1Status: 'idle',
    step2Status: 'idle',
    step3Status: 'idle',
    errorMessage: null,
    exercises: [],
    savedTopics: [],
    showCropModal: false,
    showKnowledgePointsModal: false,
    showAssignModal: false,
    selectedExerciseForDetail: null,
    selectedClasses: [],
    selectedStudents: [],
  }),
}));
